import { Grid, Menu, MenuItem } from "@mui/material";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { useAppSelector } from "app/hooks";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { FieldArray, FieldArrayRenderProps, useFormikContext } from "formik";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { selectAvaiableTranslations } from "reduxState/translations/translationsSlice";
import { FECollectionFormat } from "../types";
import { useStyles } from "./CollectionForm.styles";

interface Props {
  editMode?: boolean;
  "data-test-id"?: string;
}

export const BasicSettings = ({
  editMode,
  "data-test-id": dataTestId,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const contextMenu = useContextMenu();
  const { values } = useFormikContext<FECollectionFormat>();
  const availableLanguages = useAppSelector(selectAvaiableTranslations);

  return (
    <OLPaper className={classes.OLPaper}>
      {availableLanguages && (
        <FieldArray
          name="translations"
          render={(translationsArray: FieldArrayRenderProps) => (
            <>
              {values?.translations?.map((item, index: number) => (
                <section
                  className={`${classes.translationSection} ${classes.conditionWrapper}`}
                  key={index}
                >
                  <Grid container spacing={10}>
                    <Grid item xs>
                      <TextInput
                        className={classes.fullWidth}
                        data-test-id={`${dataTestId}-name-${item?.code}-input`}
                        disabled={editMode}
                        label={
                          index === 0 ? (
                            <RequiredFieldLabel
                              text={`${message({
                                id: "collectionName",
                              })} (${item?.code})`}
                            />
                          ) : (
                            `${message({
                              id: "collectionName",
                            })} (${item?.code})`
                          )
                        }
                        name={`translations[${index}].name`}
                      />
                    </Grid>
                    <Grid item xs>
                      <TextInput
                        className={classes.fullWidth}
                        data-test-id={`${dataTestId}-description-${item?.code}-input`}
                        label={`${message({
                          id: "collectionDescription",
                        })} (${item?.code})`}
                        multiline
                        name={`translations[${index}].description`}
                      />
                    </Grid>
                    {index > 0 ? (
                      <Grid xs="auto" item alignSelf="center">
                        <div
                          className={`${
                            values?.translations.length > 1
                              ? classes.clearIconWidth
                              : ""
                          }`}
                        >
                          <OLClearIcon
                            data-test-id={`${dataTestId}-description-${item?.code}-delete-icon`}
                            onClick={() => translationsArray.remove(index)}
                          />
                        </div>
                      </Grid>
                    ) : (
                      <Grid
                        xs="auto"
                        item
                        className={`${
                          values?.translations.length > 1
                            ? ""
                            : classes.zeroPadding
                        }`}
                      >
                        <div
                          className={`${
                            values?.translations.length > 1
                              ? classes.clearIconWidth
                              : ""
                          }`}
                        ></div>
                      </Grid>
                    )}
                  </Grid>
                </section>
              ))}
              <OLButton
                className={classes.addTranslationBtn}
                color="secondary"
                data-test-id={`${dataTestId}-add-translation-button`}
                onClick={
                  availableLanguages.filter(
                    ({ code }: { code: string }) =>
                      !values?.translations?.find(
                        (openLang: {
                          name?: string;
                          description?: string;
                          code?: string;
                        }) => openLang?.code === code,
                      ),
                  ).length
                    ? contextMenu.handleMenuOpen
                    : undefined
                }
                startIcon={<PlusIcon />}
              >
                {message({ id: "translations" })}
              </OLButton>
              <Menu
                anchorEl={contextMenu.anchor}
                anchorOrigin={{
                  horizontal: "right",
                  vertical: "top",
                }}
                onClose={contextMenu.handleMenuClose}
                open={contextMenu.isMenuOpen}
                transformOrigin={{
                  horizontal: "right",
                  vertical: "top",
                }}
                data-test-id={`${dataTestId}-language-select`}
              >
                {availableLanguages
                  .filter(
                    ({ code }: { code: string }) =>
                      !values?.translations?.find(
                        (openLang: {
                          name?: string;
                          description?: string;
                          code?: string;
                        }) => openLang?.code === code,
                      ),
                  )
                  .map(({ code, name }: { code: string; name: string }) => (
                    <MenuItem
                      data-test-id={`${dataTestId}-language-${code}`}
                      key={code}
                      onClick={() => {
                        contextMenu.handleMenuClose();
                        translationsArray.push({
                          code: code,
                        });
                      }}
                    >
                      {name}
                    </MenuItem>
                  ))}
              </Menu>
            </>
          )}
        />
      )}
    </OLPaper>
  );
};
